import type { CoinSeatsSummary } from '@/components/seats/coin-seats-card';

function ethFeesToUsd(weiAmount: bigint, ethPrice: number): number {
  return (Number(weiAmount) / 1e18) * ethPrice;
}

export interface HudSnapshot {
  tileValueUsd: number;
  totalSeats: number;
  boardCount: number;
  sinceLabel: string;
  daysPlayed: number;
  netPerWeekUsd: number;
  weeklyRewardsUsd: number;
  weeklyCostUsd: number;
  /** Lifetime tile yield (seat-holder rewards from `FeesClaimed` events).
   *  V1 ETH wei converted with the live ETH price; V2 already pre-priced
   *  to USDC (6dp) at claim time by the indexer. Excludes creator-tax
   *  distributions — those land in `lifetimeTaxesUsd` so the profile
   *  HUD can show "Rewards claimed" and "Taxes claimed" side-by-side. */
  lifetimeRewardsUsd: number;
  /** Lifetime creator-tax distributions (`CreatorFeesDistributed` events).
   *  V1 amounts come from `v1TotalCreatorFeesEarned` (ETH wei); V2 amounts
   *  come from the indexer's `v2CreatorFeesEarnedUsd` field (split out of
   *  `v2FeesEarnedUsd` in the matching schema change). Sum with
   *  `lifetimeRewardsUsd` for any "total claimed income" view. */
  lifetimeTaxesUsd: number;
}

function microToUsd(b: bigint): number {
  return Number(b) / 1e6;
}

/**
 * Snapshot row for profile HUD — uses grouped seat summary + portfolio user row.
 */
export function buildHudSnapshot(
  summary: CoinSeatsSummary,
  user: {
    createdAt?: number;
    v1TotalFeesEarned?: string;
    v2FeesEarnedUsd?: string;
    v1TotalCreatorFeesEarned?: string;
    v2CreatorFeesEarnedUsd?: string;
  },
  ethPrice: number
): HudSnapshot {
  const createdAtSec = user.createdAt;
  const since =
    createdAtSec && createdAtSec > 0 ? new Date(createdAtSec * 1000) : null;
  const sinceLabel = since
    ? since.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
    : '—';
  const daysPlayed = since
    ? Math.max(
        1,
        Math.round((Date.now() - since.getTime()) / (1000 * 60 * 60 * 24))
      )
    : 0;

  const v1FeesEth = BigInt(user.v1TotalFeesEarned || '0');
  const v2FeesMicro = BigInt(user.v2FeesEarnedUsd || '0');
  const lifetimeRewardsUsd =
    ethFeesToUsd(v1FeesEth, ethPrice) + microToUsd(v2FeesMicro);

  const v1CreatorFeesEth = BigInt(user.v1TotalCreatorFeesEarned || '0');
  const v2CreatorFeesMicro = BigInt(user.v2CreatorFeesEarnedUsd || '0');
  const lifetimeTaxesUsd =
    ethFeesToUsd(v1CreatorFeesEth, ethPrice) + microToUsd(v2CreatorFeesMicro);

  return {
    tileValueUsd: microToUsd(summary.totalSeatValue),
    totalSeats: summary.totalSeats,
    boardCount: summary.totalCoins,
    sinceLabel,
    daysPlayed,
    netPerWeekUsd: microToUsd(summary.totalWeeklyNet),
    weeklyRewardsUsd: microToUsd(summary.totalWeeklyEarnings),
    weeklyCostUsd: microToUsd(summary.totalWeeklyTax),
    lifetimeRewardsUsd,
    lifetimeTaxesUsd,
  };
}
